import React, { createContext, useContext, useState, useEffect } from 'react';
import CognitoService from '../services/CognitoService';
import AuthService from '../services/AuthService';
import { mockUserData, mockServerData } from '../services/mockData';

// Set this to true to bypass AWS Cognito and mock authentication
// Force true for development if not explicitly set to false
export const AUTH_BYPASS = process.env.REACT_APP_AUTH_BYPASS !== 'false';
console.log('AUTH_BYPASS:', AUTH_BYPASS);

const AuthContext = createContext();

export function AuthProvider({ children, bypass = AUTH_BYPASS }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState(null);
  const [userData, setUserData] = useState(null);
  const [serverData, setServerData] = useState([]);
  const [wsConnection, setWsConnection] = useState(null);
  // CSRF token state removed
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check for POST authentication data on component mount
  useEffect(() => {
    /**
     * Check for server-injected authentication data (window.postAuthData)
     * which takes precedence over session storage data
     */
    const checkServerInjectedData = () => {
      try {
        // Check if server has injected authentication data via template
        if (window.postAuthData) {
          console.log('[AuthContext] Found server-injected authentication data');
          
          const postData = window.postAuthData;
          
          // Save the data to session storage for future page loads
          AuthService.savePostAuthData(postData);
          
          // CSRF token handling removed
          
          // Update all authentication state
          setToken(postData.token || null);
          setUserData(postData.userData || null);
          setServerData(postData.serverData || []);
          setWsConnection(postData.wsConnection || null);
          setIsAuthenticated(true);
          
          return true;
        }
        return false;
      } catch (error) {
        console.error('[AuthContext] Error processing server-injected data:', error);
        return false;
      }
    };
    
    const checkPostAuth = async () => {
      try {
        // First check for server-injected data which takes precedence
        const hasServerInjectedData = checkServerInjectedData();
        
        // If no server-injected data, check for CSRF token in meta tags
        if (!hasServerInjectedData) {
          // Check if we have POST auth data in session storage
          const postAuthData = AuthService.getPostAuthData();
          
          if (postAuthData) {
            console.log('[AuthContext] Found authentication data in session storage');
            
            // We have POST auth data, set the authenticated state
            setToken(postAuthData.token);
            setUserData(postAuthData.userData);
            
            // Use provided server data if available, otherwise use mock server data
            if (postAuthData.serverData && postAuthData.serverData.length > 0) {
              setServerData(postAuthData.serverData);
            } else if (bypass) {
              console.log('[AuthContext] No server data provided, using mock server data');
              setServerData(mockServerData);
            }
            
            // Set WebSocket connection information if available
            if (postAuthData.wsConnection) {
              console.log('[AuthContext] WebSocket connection info found in postAuthData');
              setWsConnection(postAuthData.wsConnection);
            } else {
              console.log('[AuthContext] No WebSocket connection info in postAuthData');
            }
            
            setIsAuthenticated(true);
          } else {
            // No authentication data found anywhere
            console.log('[AuthContext] No authentication data found');
            setIsAuthenticated(false);
          }
        }
        // Removed automatic authentication in bypass mode
        // Now users must always go through the login page
      } catch (err) {
        console.error('[AuthContext] Error checking authentication:', err);
        setError('Authentication error. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    checkPostAuth();
  }, [bypass]);

  // Mock validation for development
  const mockValidate = (token) => {
    return token && token.length > 0;
  };

  // Real AWS Cognito validation
  const cognitoValidate = async (token) => {
    return await CognitoService.validateToken(token);
  };

  // Form-based login
  const login = async (token, forcedBypass = false) => {
    setError(null);
    setLoading(true);
    
    try {
      let valid = false;
      
      // Use either the global bypass setting or the forced bypass parameter
      const shouldBypass = forcedBypass || bypass;
      
      if (shouldBypass) {
        valid = mockValidate(token);
        console.log('Using mock validation (bypass mode):', valid);
      } else {
        valid = await cognitoValidate(token);
        console.log('Using Cognito validation:', valid);
      }
      
      if (valid) {
        setIsAuthenticated(true);
        setToken(token);
        return true;
      } else {
        setError('Invalid authentication token.');
        setIsAuthenticated(false);
        setToken(null);
        return false;
      }
    } catch (err) {
      setError('Authentication error. Please try again.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // POST request-based login
  const loginWithPostData = async (postData, forcedBypass = false) => {
    console.log('%c AuthContext: Processing login with POST data', 'background: #2196F3; color: white; padding: 2px 5px; border-radius: 3px;');
    console.log('AuthContext: POST data received:', JSON.stringify(postData, null, 2));
    setError(null);
    setLoading(true);
    
    try {
      // Check if data has been pre-validated by the server
      const isServerValidated = postData.serverValidated === true;
      if (isServerValidated) {
        console.log('%c AuthContext: Using server-validated token - skipping client validation', 'background: #4CAF50; color: white; padding: 2px 5px; border-radius: 3px;');
      }
      
      // If we're bypassing auth, skip the actual validation
      if (forcedBypass || bypass || isServerValidated) {
        console.log('Bypassing POST auth validation for testing');
        
        // Extract user data from the POST request
        const userData = postData.user_data || {};
        
        // Extract server data from the POST request if available
        let serverDataToUse = [];
        
        if (postData.server_data && postData.server_data.length > 0) {
          // Use the server data provided in the POST request
          serverDataToUse = postData.server_data;
          console.log('Using server data from POST request');
        } else if (userData && Object.keys(userData).length > 0) {
          // If user data is provided but no server data, use mock server data
          serverDataToUse = mockServerData;
          console.log('No server data provided in POST request, using mock server data');
        }
        
        // Extract WebSocket connection information if available
        // Accept both snake_case and camelCase for robustness
        const wsConnectionToUse = postData.ws_connection || postData.wsConnection || null;
        console.log('%c AuthContext: WebSocket connection info from POST data', 'background: #9C27B0; color: white; padding: 2px 5px; border-radius: 3px;');
        console.log('AuthContext: wsConnectionToUse:', JSON.stringify(wsConnectionToUse, null, 2));
        
        // Create a simulated successful auth result
        const authResult = {
          token: postData.token || 'test-token',
          userData: userData,
          serverData: serverDataToUse,
          wsConnection: wsConnectionToUse,
          // CSRF token field removed
        };
        
        // Save the auth data to session storage
        AuthService.savePostAuthData(authResult);
        
        // Update state
        setToken(authResult.token);
        setUserData(authResult.userData);
        setServerData(authResult.serverData);

        // Set WebSocket connection with logging
        console.log('%c AuthContext: Setting WebSocket connection from auth result', 'background: #4CAF50; color: white; padding: 2px 5px; border-radius: 3px;');
        console.log('AuthContext: wsConnection value:', JSON.stringify(authResult.wsConnection, null, 2));
        setWsConnection(authResult.wsConnection);
        
        setIsAuthenticated(true);
        
        return true;
      } else {
        // Normal flow - validate with Cognito
        const authResult = await AuthService.processPostAuth(postData);
        
        // Check if server data is available in the auth result
        if (!authResult.serverData || authResult.serverData.length === 0) {
          // If no server data is provided but we have user data, use mock server data
          if (authResult.userData && Object.keys(authResult.userData).length > 0) {
            console.log('No server data in auth result, using mock server data');
            authResult.serverData = mockServerData;
          }
        }
        
        // Save the auth data to session storage
        AuthService.savePostAuthData(authResult);
        
        // Update state
        setToken(authResult.token);
        setUserData(authResult.userData);
        setServerData(authResult.serverData);
        
        // Set WebSocket connection with logging
        console.log('%c AuthContext: Setting WebSocket connection from auth result', 'background: #4CAF50; color: white; padding: 2px 5px; border-radius: 3px;');
        console.log('AuthContext: wsConnection value:', JSON.stringify(authResult.wsConnection, null, 2));
        setWsConnection(authResult.wsConnection);
        
        setIsAuthenticated(true);
        
        return true;
      }
    } catch (err) {
      setError(err.message || 'Authentication error. Please try again.');
      setIsAuthenticated(false);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    // Clear auth state
    setIsAuthenticated(false);
    setToken(null);
    setUserData(null);
    setServerData([]);
    setWsConnection(null);
    // CSRF token reset removed
    setError(null);
    
    // Clear stored POST auth data
    AuthService.clearPostAuthData();
  };

  return (
    <AuthContext.Provider value={{ 
      isAuthenticated, 
      token, 
      userData,
      serverData,
      wsConnection,
      // CSRF token context value removed
      login, 
      loginWithPostData,
      logout, 
      error,
      loading
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
